import React from "react";

type IncidentTypeDisplayProps = {
    categoryName?: string;
    typeName?: string;
    tags?: string[];
    onTagClick?: (tag: string) => void;
}

export const IncidentTypeDisplay: React.FC<IncidentTypeDisplayProps> = ({
    categoryName,
    typeName,
    tags,
    onTagClick,
}) => {
    // Convert snake_case to Title Case
    const formatTag = (tag: string): string => {
        return tag
            .split("_")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    // Incidents use either type/category OR tags, not both
    const hasType = typeName || categoryName;
    const hasTags = tags && tags.length > 0;

    if (hasType) {
        // Display category and type
        return (
            <div className="flex flex-col gap-1">
                {categoryName && (
                    <span className="text-xs text-grey">{categoryName}</span>
                )}
                {typeName && (
                    <span className="font-medium">{typeName}</span>
                )}
            </div>
        );
    }

    const SEVERITY_TAG_CLASSES: Record<string, string> = {
        high_severity: "bg-purple text-white border-purple",
        medium_severity: "bg-dark-orange text-white border-dark-orange",
        low_severity: "bg-green text-white border-green",
        unknown_severity: "bg-grey text-white border-grey",
    };

    if (hasTags) {
        // Severity tags first, then remaining tags alphabetically
        const severityTags = tags.filter(t => t.endsWith("_severity"));
        const otherTags = [...tags.filter(t => !t.endsWith("_severity"))].sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase())
        );
        const sortedTags = [...severityTags, ...otherTags];

        return (
            <div className="flex flex-wrap gap-1.5">
                {sortedTags.map((tag, index) => {
                    const severityClass = SEVERITY_TAG_CLASSES[tag];
                    return (
                        <span
                            key={index}
                            onClick={onTagClick ? (e) => { e.stopPropagation(); onTagClick(tag); } : undefined}
                            className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border shadow-sm transition-all duration-200 ${
                                severityClass
                                    ? severityClass
                                    : "bg-white-blue text-dark-blue border-light-blue hover:shadow-md hover:bg-light-blue hover:border-blue"
                            } ${onTagClick ? "cursor-pointer" : ""}`}
                        >
                            {formatTag(tag)}
                        </span>
                    );
                })}
            </div>
        );
    }

    return <span className="text-grey">N/A</span>;
};
